import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, RefreshCw, Search } from 'lucide-react'
import { SALON_NOTES_LIST_LIMIT, salonStages, type SalonRow } from '@manaro/contracts'
import { DecisionNotice, type Notice } from '@/features/salons/components/decision-notice.tsx'
import { canGrantPlans } from '@manaro/contracts'
import { useSession } from '@/features/auth/use-session.ts'
import { ModerationDialog } from '@/features/salons/components/moderation-dialog.tsx'
import { SalonNotesDialog } from '@/features/salons/components/notes-dialog.tsx'
import { PlanGrantDialog } from '@/features/salons/components/plan-grant-dialog.tsx'
import { planGrantNotice } from '@/features/salons/plan-grant.ts'
import { SalonsTable, rowKey } from '@/features/salons/components/salons-table.tsx'
import { downloadSalonsCsv } from '@/features/salons/export-csv.ts'
import { countBy, facetSalons, type StageFilter } from '@/features/salons/facets.ts'
import { CHANNEL_ORDER, NO_SIGNUP_CHANNEL, STAGE_LABELS, channelLabel } from '@/features/salons/labels.ts'
import {
  CATALOG_FILTERS,
  MODERATION_FILTER_LABELS,
  decisionNotice,
  isModeratable,
  moderationState,
  type CatalogFilter,
  type ModeratableRow,
} from '@/features/salons/moderation.ts'
import { groupNotesByOwner, rowNotes, type RowNotes } from '@/features/salons/notes.ts'
import { findNotesRow } from '@/features/salons/reminders.ts'
import { matchesSalonSearch, normalizeSalonSearch } from '@/features/salons/search.ts'
import { useSalonNotes } from '@/features/salons/use-salon-notes.ts'
import { useRefreshSalons, useSalons } from '@/features/salons/use-salons.ts'
import { ApiError } from '@/shared/api/client.ts'
import { formatTime } from '@/shared/lib/format.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { NativeSelect, type SelectOption } from '@/shared/ui/native-select.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

const channelOf = (row: SalonRow): string => row.user.signup?.channel ?? NO_SIGNUP_CHANNEL


const countedOption = <T extends string>(value: T, label: string, count: number): SelectOption<T> => ({
  value,
  label: `${label} · ${count}`,
})

export function SalonsPage() {
  const { data, isPending, error, refetch } = useSalons()
  const { mutate: refresh, isPending: isRefreshing } = useRefreshSalons()
  const [stage, setStage] = useState<StageFilter>('all')
  const [channel, setChannel] = useState('all')
  const [search, setSearch] = useState('')
  const [showTests, setShowTests] = useState(false)
  const [catalog, setCatalog] = useState<CatalogFilter>('all')
  const [moderatingId, setModeratingId] = useState<number | null>(null)
  const [grantingId, setGrantingId] = useState<number | null>(null)
  const [notingRow, setNotingRow] = useState<SalonRow | null>(null)
  const notesQuery = useSalonNotes()
  const access = useSession().data?.user.access
  const mayGrant = access !== undefined && canGrantPlans(access)
  const [notice, setNotice] = useState<Notice | null>(null)
  const hideNotice = useCallback(() => setNotice(null), [])

  const allRows = useMemo(() => data?.rows ?? [], [data])
  const notesByOwner = useMemo(() => groupNotesByOwner(notesQuery.data?.notes ?? []), [notesQuery.data])
  const notesFor = useCallback((row: SalonRow) => rowNotes(notesByOwner, row), [notesByOwner])
  const rowsNotes = useMemo<RowNotes | undefined>(
    () => (notesQuery.data?.available ? { forRow: notesFor, onOpen: setNotingRow } : undefined),
    [notesQuery.data?.available, notesFor],
  )
  const testCount = useMemo(() => allRows.filter((row) => row.user.looksLikeTest).length, [allRows])
  const hasModeration = useMemo(() => allRows.some(isModeratable), [allRows])

  const baseRows = useMemo(() => {
    const query = normalizeSalonSearch(search)
    return allRows.filter(
      (row) => (showTests || !row.user.looksLikeTest) && matchesSalonSearch(row, query, notesFor(row)),
    )
  }, [allRows, search, showTests, notesFor])

  const channelCounts = useMemo(() => countBy(baseRows, channelOf), [baseRows])
  const channels = CHANNEL_ORDER.filter((value) => channelCounts[value] || value === channel)
  const hasSignupData = channels.some((value) => value !== NO_SIGNUP_CHANNEL)

  const channelRows = useMemo(
    () => (channel === 'all' ? baseRows : baseRows.filter((row) => channelOf(row) === channel)),
    [baseRows, channel],
  )
  const { rows, stageTotal, stageCounts, catalogTotal, catalogCounts } = useMemo(
    () => facetSalons(channelRows, stage, catalog),
    [channelRows, stage, catalog],
  )
  // The dialog follows the live row: after a refetch it shows what another moderator already decided.
  const moderating = useMemo(
    () => allRows.find((row): row is ModeratableRow => isModeratable(row) && row.salon.id === moderatingId) ?? null,
    [allRows, moderatingId],
  )
  const showChannels = hasSignupData || channel !== 'all'
  const catalogFilters = CATALOG_FILTERS.filter((value) => catalogCounts[value] || value === catalog)
  const granting = useMemo(
    () => allRows.find((row) => row.salon?.id === grantingId)?.salon ?? null,
    [allRows, grantingId],
  )
  // Follows the live row and its notes; a row gone from a refreshed list keeps the dialog and the draft open.
  const noting = useMemo(
    () => (notingRow ? (allRows.find((row) => rowKey(row) === rowKey(notingRow)) ?? notingRow) : null),
    [allRows, notingRow],
  )

  // «Открыть комментарии» from a reminder lands here with ?notes=<owner>-<salon>: open that row once the list is in.
  const [searchParams, setSearchParams] = useSearchParams()
  const notesLink = searchParams.get('notes')
  useEffect(() => {
    if (!notesLink || !data) return
    const row = findNotesRow(data.rows, notesLink)
    if (row) setNotingRow(row)
    else setNotice({ text: 'Строки для этого комментария в списке нет — возможно, салон удалён.', sticky: false })
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.delete('notes')
        return next
      },
      { replace: true },
    )
  }, [notesLink, data, setSearchParams])
  const waitingReview = useMemo(() => baseRows.filter((row) => moderationState(row) === 'pending').length, [baseRows])

  // Phones get the same filters as two native pickers: a dozen chips would push the list off the first screen.
  const stageOptions: Array<SelectOption<StageFilter>> = [
    countedOption('all', 'Все', stageTotal),
    ...salonStages.map((value) => countedOption(value, STAGE_LABELS[value], stageCounts[value] ?? 0)),
  ]
  const channelOptions = [
    countedOption('all', 'Все', baseRows.length),
    ...channels.map((value) => countedOption(value, channelLabel(value), channelCounts[value] ?? 0)),
  ]
  const catalogOptions: Array<SelectOption<CatalogFilter>> = [
    countedOption('all', 'Все', catalogTotal),
    ...catalogFilters.map((value) => countedOption(value, MODERATION_FILTER_LABELS[value], catalogCounts[value] ?? 0)),
  ]

  // The banner counts every salon on review, so it also clears the stage and source filters.
  const showWaiting = () => {
    setCatalog('pending')
    setStage('all')
    setChannel('all')
  }

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[300px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Салоны
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Кто зарегистрировался и докуда дошёл: салон, услуги, клиенты, записи, оплата.
            {' '}Без салона — только регистрации за полгода, без мастеров и клиентов существующих салонов.
            {data && data.catalogHidden > 0 && ` Каталог, который заводили менеджеры (${data.catalogHidden}), здесь не показан.`}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto max-md:[&>button]:min-h-11 max-md:[&>button]:flex-1">
          <button
            type="button"
            onClick={() => refresh()}
            disabled={isRefreshing || isPending}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-surface-soft px-3 py-1.5 text-[13px] whitespace-nowrap text-ink-muted transition-colors hover:text-ink disabled:opacity-60"
          >
            <RefreshCw className={isRefreshing ? 'size-3.5 animate-spin' : 'size-3.5'} />
            {isRefreshing ? 'Считаем…' : data?.generatedAt ? `Посчитано в ${formatTime(data.generatedAt)}` : 'Обновить'}
          </button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="size-3.5" />}
            disabled={rows.length === 0}
            onClick={() => downloadSalonsCsv(rows)}
          >
            Скачать CSV
          </Button>
        </div>
      </header>

      {data && !data.available ? (
        <Alert>
          Бэкенд Manaro не подключён: в настройках сервера не заданы <code>MANARO_API_URL</code> и{' '}
          <code>MANARO_STATS_TOKEN</code>.
        </Alert>
      ) : (
        <div className="flex flex-col gap-4">
          {data && (
            <div className="grid grid-cols-2 gap-x-2 gap-y-2.5 md:hidden">
              <label className={showChannels ? 'min-w-0' : 'col-span-2'}>
                <span className="mb-1 block text-[12px] text-ink-muted">Стадия</span>
                <NativeSelect value={stage} options={stageOptions} onChange={setStage} highlighted={stage !== 'all'} />
              </label>
              {showChannels && (
                <label className="min-w-0">
                  <span className="mb-1 block text-[12px] text-ink-muted">Откуда</span>
                  <NativeSelect
                    value={channel}
                    options={channelOptions}
                    onChange={setChannel}
                    highlighted={channel !== 'all'}
                  />
                </label>
              )}
              {hasModeration && (
                <label className="col-span-2 min-w-0">
                  <span className="mb-1 block text-[12px] text-ink-muted">Каталог</span>
                  <NativeSelect
                    value={catalog}
                    options={catalogOptions}
                    onChange={setCatalog}
                    highlighted={catalog !== 'all'}
                  />
                </label>
              )}
              {testCount > 0 && (
                <div className="col-span-2 [&>button]:min-h-11">
                  <Chip
                    label="Тестовые"
                    count={testCount}
                    tone="tint"
                    active={showTests}
                    onClick={() => setShowTests((value) => !value)}
                  />
                </div>
              )}
            </div>
          )}

          {data && (
            <div className="hidden flex-wrap items-center gap-2 md:flex">
              <Chip label="Все" count={stageTotal} active={stage === 'all'} onClick={() => setStage('all')} />
              {salonStages.map((value) => (
                <Chip
                  key={value}
                  label={STAGE_LABELS[value]}
                  count={stageCounts[value] ?? 0}
                  active={stage === value}
                  onClick={() => setStage(value)}
                />
              ))}
              {testCount > 0 && (
                <Chip
                  label="Тестовые"
                  count={testCount}
                  tone="tint"
                  active={showTests}
                  onClick={() => setShowTests((value) => !value)}
                />
              )}
            </div>
          )}

          {showChannels && (
            <div className="hidden flex-wrap items-center gap-2 md:flex">
              <span className="mr-1 text-[13px] text-ink-muted">Откуда</span>
              <Chip
                label="Все"
                count={baseRows.length}
                tone="tint"
                active={channel === 'all'}
                onClick={() => setChannel('all')}
              />
              {channels.map((value) => (
                <Chip
                  key={value}
                  label={channelLabel(value)}
                  count={channelCounts[value] ?? 0}
                  tone="tint"
                  active={channel === value}
                  onClick={() => setChannel(value)}
                />
              ))}
            </div>
          )}

          {hasModeration && (
            <div className="hidden flex-wrap items-center gap-2 md:flex">
              <span className="mr-1 text-[13px] text-ink-muted">Каталог</span>
              <Chip
                label="Все"
                count={catalogTotal}
                tone="tint"
                active={catalog === 'all'}
                onClick={() => setCatalog('all')}
              />
              {catalogFilters.map((value) => (
                <Chip
                  key={value}
                  label={MODERATION_FILTER_LABELS[value]}
                  count={catalogCounts[value] ?? 0}
                  tone="tint"
                  active={catalog === value}
                  onClick={() => setCatalog(value)}
                />
              ))}
            </div>
          )}

          {waitingReview > 0 && catalog !== 'pending' && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-warn-line bg-warn-tint px-3.5 py-2.5 text-[14px] text-warn-ink">
              <span className="min-w-0 flex-1">
                Ждут проверки перед публикацией в каталоге: <span className="tabular font-semibold">{waitingReview}</span>
              </span>
              <Button size="sm" className="max-md:min-h-11 max-md:w-full" onClick={showWaiting}>
                Показать
              </Button>
            </div>
          )}

          <DecisionNotice notice={notice} onClose={hideNotice} />

          {notesQuery.data?.truncated && (
            <Alert>
              Комментариев так много, что показаны и ищутся только последние{' '}
              {SALON_NOTES_LIST_LIMIT.toLocaleString('ru-RU')}. Напишите разработчикам — пора подгружать их частями.
            </Alert>
          )}

          {notesQuery.error && (
            <Alert>
              Комментарии не загрузились:{' '}
              {notesQuery.error instanceof ApiError ? notesQuery.error.message : 'сервер не ответил.'}{' '}
              <button type="button" className="font-medium underline" onClick={() => void notesQuery.refetch()}>
                Повторить
              </button>
            </Alert>
          )}

          <Card className="overflow-hidden">
            <label className="flex items-center gap-2.5 border-b border-line-soft px-4 py-3.5 md:px-5">
              <Search className="size-4 shrink-0 text-ink-faint" aria-hidden />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={
                  rowsNotes
                    ? 'Поиск по почте, имени, телефону, @нику, салону, ID или комментарию…'
                    : 'Поиск по почте, имени, телефону, @нику, салону или ID…'
                }
                className="w-full bg-transparent text-[16px] outline-none placeholder:text-ink-faint md:text-[14.5px]"
              />
            </label>

            {isPending ? (
              <div className="flex flex-col gap-3 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : error ? (
              <ErrorState
                message={error instanceof ApiError ? error.message : 'Не удалось загрузить салоны.'}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                title={catalog === 'pending' ? 'Все салоны проверены' : 'Никого не нашли'}
                description={
                  catalog === 'pending'
                    ? 'Новых салонов на проверке нет.'
                    : 'Попробуйте другой запрос или снимите фильтры.'
                }
              />
            ) : (
              <SalonsTable
                rows={rows}
                onModerate={
                  hasModeration
                    ? (row) => {
                        if (isModeratable(row)) setModeratingId(row.salon.id)
                      }
                    : undefined
                }
                onGrant={mayGrant ? setGrantingId : undefined}
                notes={rowsNotes}
              />
            )}
          </Card>
        </div>
      )}

      {granting && (
        <PlanGrantDialog
          key={granting.id}
          salon={granting}
          onClose={() => setGrantingId(null)}
          onDone={(result) => setNotice({ text: planGrantNotice(granting.title, result), sticky: false })}
        />
      )}

      {noting && (
        <SalonNotesDialog
          key={rowKey(noting)}
          row={noting}
          notes={notesFor(noting)}
          onClose={() => setNotingRow(null)}
        />
      )}

      {moderating && (
        <ModerationDialog
          key={moderating.salon.id}
          row={moderating}
          onClose={() => setModeratingId(null)}
          onDone={(result) =>
            setNotice({
              text: decisionNotice(moderating.salon.title, moderating.user.email, result),
              sticky: !result.ownerNotified,
            })
          }
        />
      )}
    </>
  )
}
